import { calendarDateKey } from "@calendar/calendarLayout";
import type { ScheduleSearch } from "@calendar/scheduleSearch";
import {
  formatRange,
  mondayOf,
  parseDateParam,
  rangeDays,
  shiftDays,
  toISODate,
} from "@calendar/weekDates";
import { WeekView } from "@calendar/WeekView";
import { useClinic } from "@clinic/clinicQueries";
import { AsyncBoundary } from "@components/AsyncBoundary";
import {
  Button,
  ChevronLeftIcon,
  ChevronRightIcon,
  cn,
  IconButton,
  PanelError,
  SegmentedControl,
} from "@scribenote/brian-design-system";
import { useLayoutEffect, useRef, useState } from "react";

const dayCountForWidth = (width: number): 1 | 3 | 7 => {
  if (width === 0) return 7;
  if (width < 640) return 1;
  if (width < 1024) return 3;
  return 7;
};

const DAY_OPTIONS: { value: 1 | 3 | 7; label: string }[] = [
  { value: 1, label: "Day" },
  { value: 3, label: "3-Day" },
  { value: 7, label: "Week" },
];

const TOOL =
  "rounded-md border-[1.5px] border-rule-strong px-[22px] font-semibold text-ink";

const prefersReducedMotion = (): boolean =>
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;

type FlipDirection = "prev" | "next";

interface PageSnapshot {
  key: string;
  anchorKey: string;
  anchor: Date;
  dayCount: number;
}

interface CalendarShellProps {
  search: ScheduleSearch;
  onSearchChange: (search: ScheduleSearch) => void;
}

export const CalendarShell = ({
  search,
  onSearchChange,
}: CalendarShellProps) => {
  const clinic = useClinic();
  if (clinic.isPending) return <WeekSkeleton dayCount={7} />;
  if (!clinic.data)
    return (
      <PanelError label="calendar" onRetry={() => void clinic.refetch()} />
    );
  return (
    <CalendarContent
      timeZone={clinic.data.timezone}
      search={search}
      onSearchChange={onSearchChange}
    />
  );
};

const CalendarContent = ({
  timeZone,
  search: { week, days },
  onSearchChange,
}: CalendarShellProps & { timeZone: string }) => {
  const rootRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    const observer = new ResizeObserver(() => setWidth(el.clientWidth));
    observer.observe(el);
    setWidth(el.clientWidth);
    return () => observer.disconnect();
  }, []);

  const dayCount = days ?? dayCountForWidth(width);
  const todayKey = calendarDateKey(new Date(), timeZone);
  const selectedDate = parseDateParam(week ?? todayKey);
  const anchor = dayCount === 7 ? mondayOf(selectedDate) : selectedDate;
  const anchorKey = toISODate(anchor);
  const visibleDays = rangeDays(anchor, dayCount);
  const contentKey = `${anchorKey}:${dayCount}`;

  // Page flip: a keyed incoming page plus a decorative outgoing overlay, never mounted under reduced motion.
  const [page, setPage] = useState<PageSnapshot>({
    key: contentKey,
    anchorKey,
    anchor,
    dayCount,
  });
  const [flip, setFlip] = useState<FlipDirection | null>(null);
  const [outgoing, setOutgoing] = useState<
    (PageSnapshot & { direction: FlipDirection }) | null
  >(null);
  if (page.key !== contentKey) {
    const direction: FlipDirection =
      anchorKey < page.anchorKey ? "prev" : "next";
    setPage({ key: contentKey, anchorKey, anchor, dayCount });
    // A density-only change plays the settle but lifts no page away.
    setFlip(direction);
    if (anchorKey !== page.anchorKey && !prefersReducedMotion()) {
      setOutgoing({ ...page, direction });
    }
  }

  const goTo = (target: Date) => {
    const normalized = dayCount === 7 ? mondayOf(target) : target;
    onSearchChange({ week: toISODate(normalized), days });
  };

  // Leaving Week view lands on today when visible, not the Monday-snapped anchor.
  const setDays = (value: 1 | 3 | 7) => {
    const targetKey =
      dayCount === 7 &&
      value !== 7 &&
      visibleDays.some((day) => toISODate(day) === todayKey)
        ? todayKey
        : anchorKey;
    onSearchChange({ week: targetKey, days: value });
  };

  return (
    <div
      ref={rootRef}
      className="flex min-h-0 w-full flex-1 flex-col overflow-clip"
    >
      <section
        aria-label="Schedule"
        className="flex min-h-0 min-w-0 flex-1 flex-col gap-2.5"
      >
        <h1 className="sr-only">Schedule</h1>
        <nav
          aria-label="Calendar controls"
          className="grid shrink-0 grid-cols-2 items-center gap-3 min-[1100px]:grid-cols-[1fr_auto_1fr]"
        >
          <div className="order-2 flex justify-start min-[1100px]:order-1">
            <Button
              variant="outline"
              onClick={() => goTo(parseDateParam(todayKey))}
              data-testid="calendar-this-week"
              className={cn(TOOL, "min-h-9")}
            >
              Today
            </Button>
          </div>
          <div className="order-1 col-span-2 flex items-center justify-center gap-4 min-[1100px]:order-2 min-[1100px]:col-span-1">
            <IconButton
              variant="outline"
              aria-label="Previous"
              onClick={() => goTo(shiftDays(anchor, -dayCount))}
              data-testid="calendar-prev-week"
              className={cn(TOOL, "size-10 px-0")}
            >
              <ChevronLeftIcon />
            </IconButton>
            <h2
              aria-live="polite"
              className="min-w-56 text-center font-display text-[1.375rem] font-medium tracking-[-0.01em] text-ink"
            >
              {formatRange(visibleDays[0], visibleDays[dayCount - 1])}
            </h2>
            <IconButton
              variant="outline"
              aria-label="Next"
              onClick={() => goTo(shiftDays(anchor, dayCount))}
              data-testid="calendar-next-week"
              className={cn(TOOL, "size-10 px-0")}
            >
              <ChevronRightIcon />
            </IconButton>
          </div>
          <div className="order-3 flex justify-end">
            <SegmentedControl
              aria-label="Days shown"
              options={DAY_OPTIONS}
              value={dayCount}
              onValueChange={setDays}
              className="gap-1.5 [&>button]:min-h-10 [&>button]:rounded-md [&>button]:border-[1.5px] [&>button]:px-[22px] [&>button]:font-semibold [&>button[aria-checked=false]]:border-rule-strong"
            />
          </div>
        </nav>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-rule bg-card shadow-sheet">
          <div className="relative min-h-0 flex-1 overflow-hidden">
            {/* bg-card here keeps the `prev` page opaque over the outgoing one. */}
            <div
              key={contentKey}
              className={cn(
                "relative flex h-full min-h-0 flex-col bg-card",
                outgoing?.direction === "prev" && "z-10",
                flip !== null &&
                  !prefersReducedMotion() &&
                  (flip === "next"
                    ? "animate-week-flip-in-next"
                    : "animate-week-flip-in-prev"),
              )}
            >
              <AsyncBoundary
                skeleton={<WeekSkeleton dayCount={dayCount} />}
                fallback={(retry) => (
                  <PanelError label="calendar" onRetry={retry} />
                )}
                resetKeys={[anchorKey, dayCount]}
              >
                <WeekView
                  anchor={anchor}
                  dayCount={dayCount}
                  timeZone={timeZone}
                />
              </AsyncBoundary>
            </div>

            {outgoing && (
              <div
                aria-hidden="true"
                inert
                onAnimationEnd={(e) => {
                  // Child animations bubble; only the overlay's own flip ends it.
                  if (e.animationName.startsWith("week-flip-out")) {
                    setOutgoing(null);
                  }
                }}
                className={cn(
                  "pointer-events-none absolute inset-0 flex min-h-0 flex-col bg-card",
                  outgoing.direction === "next"
                    ? "z-10 animate-week-flip-out-next"
                    : "animate-week-flip-out-prev",
                )}
              >
                <AsyncBoundary
                  skeleton={<WeekSkeleton dayCount={outgoing.dayCount} />}
                  fallback={() => null}
                >
                  <WeekView
                    anchor={outgoing.anchor}
                    timeZone={timeZone}
                    dayCount={outgoing.dayCount}
                  />
                </AsyncBoundary>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

// Ghost-card top/height as % of the column.
const SKELETON_GHOSTS = [
  [
    { top: 12, height: 14 },
    { top: 55, height: 20 },
  ],
  [{ top: 30, height: 18 }],
  [
    { top: 8, height: 12 },
    { top: 40, height: 16 },
    { top: 70, height: 14 },
  ],
  [
    { top: 22, height: 22 },
    { top: 62, height: 12 },
  ],
  [{ top: 45, height: 18 }],
  [
    { top: 15, height: 16 },
    { top: 52, height: 24 },
  ],
  [{ top: 35, height: 20 }],
];

const WeekSkeleton = ({ dayCount }: { dayCount: number }) => (
  <div className="flex h-full flex-col">
    <div className="flex border-b border-rule bg-wall">
      <div className="w-14 shrink-0" />
      {Array.from({ length: dayCount }).map((_, i) => (
        <div
          key={i}
          className="flex flex-1 flex-col items-center gap-1.5 px-1 py-2"
        >
          <div
            className="h-2.5 w-8 animate-pulse rounded-full bg-recess motion-reduce:animate-none"
            style={{ animationDelay: `${i * 70}ms` }}
          />
          <div
            className="h-7 w-7 animate-pulse rounded-full bg-recess motion-reduce:animate-none"
            style={{ animationDelay: `${i * 70 + 40}ms` }}
          />
        </div>
      ))}
    </div>

    <div className="flex min-h-0 flex-1">
      <div className="flex w-14 shrink-0 flex-col justify-between py-3">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="mr-1 ml-auto h-2 w-6 animate-pulse rounded-full bg-recess motion-reduce:animate-none"
            style={{ animationDelay: `${i * 60}ms` }}
          />
        ))}
      </div>

      {Array.from({ length: dayCount }).map((_, col) => (
        <div key={col} className="relative flex-1 border-l border-rule">
          {Array.from({ length: 6 }).map((_, r) => (
            <div
              key={r}
              className="absolute inset-x-0 border-t border-rule"
              style={{ top: `${(r / 6) * 100}%` }}
            />
          ))}
          {SKELETON_GHOSTS[col % SKELETON_GHOSTS.length].map((g, gi) => (
            <div
              key={gi}
              className="absolute inset-x-[3px] animate-pulse overflow-hidden rounded-[4px] bg-recess motion-reduce:animate-none"
              style={{
                top: `${g.top}%`,
                height: `${g.height}%`,
                animationDelay: `${(col * 2 + gi) * 90}ms`,
              }}
            >
              <span className="absolute left-0 top-0 h-full w-[3px] bg-hollow" />
            </div>
          ))}
        </div>
      ))}
    </div>
  </div>
);
